const MONTHS: Record<string, number> = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
}

const DAYS_BEFORE_EXPIRY = 60
const DAYS_AFTER_EXPIRY = 30

export type PpedPolicyDates = {
    od_expiry_date?: string | null
    tp_expiry_date?: string | null
}

export type PpedExpirySource = 'od' | 'tp'

export type ChallanPPEDBracket = {
    isInBracket: boolean
    today: string
    od_expiry_date: string
    expirySource: PpedExpirySource
    lowerBound: string
    upperBound: string
    daysFromExpiry: number
}

const startOfDay = (date: Date) => {
    const next = new Date(date)
    next.setHours(0, 0, 0, 0)
    return next
}

const parseExpiryDate = (value: unknown): Date | null => {
    if (value == null || value === '') return null

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null
        return startOfDay(value)
    }

    if (typeof value !== 'string') return null

    const trimmed = value.trim()
    const dmy = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/)
    if (dmy) {
        const day = Number(dmy[1])
        const month = MONTHS[dmy[2].toLowerCase()]
        const year = Number(dmy[3])
        if (month == null) return null
        const date = new Date(year, month, day)
        if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null
        return startOfDay(date)
    }

    const parsed = new Date(trimmed)
    if (Number.isNaN(parsed.getTime())) return null
    return startOfDay(parsed)
}

export const resolvePpedExpiryDate = (
    policyData?: PpedPolicyDates | null
): { expiryDate: Date; source: PpedExpirySource } | null => {
    const od = parseExpiryDate(policyData?.od_expiry_date)
    const tp = parseExpiryDate(policyData?.tp_expiry_date)

    if (od && !tp) return { expiryDate: od, source: 'od' }
    if (tp && !od) return { expiryDate: tp, source: 'tp' }
    if (!od || !tp) return null

    if (od.getTime() <= tp.getTime()) return { expiryDate: od, source: 'od' }
    return { expiryDate: tp, source: 'tp' }
}

export const getChallanPPEDBracket = (policyData?: PpedPolicyDates | null): ChallanPPEDBracket | null => {
    const resolved = resolvePpedExpiryDate(policyData)
    if (!resolved) return null

    const { expiryDate, source } = resolved
    const today = startOfDay(new Date())

    const lowerBound = new Date(expiryDate)
    lowerBound.setDate(lowerBound.getDate() - DAYS_BEFORE_EXPIRY)

    const upperBound = new Date(expiryDate)
    upperBound.setDate(upperBound.getDate() + DAYS_AFTER_EXPIRY)

    const inBracket = today >= lowerBound && today <= upperBound

    return {
        isInBracket: inBracket,
        today: today.toDateString(),
        od_expiry_date: expiryDate.toDateString(),
        expirySource: source,
        lowerBound: lowerBound.toDateString(),
        upperBound: upperBound.toDateString(),
        daysFromExpiry: Math.round((today.getTime() - expiryDate.getTime()) / (1000 * 60 * 60 * 24)),
    }
}
